import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import {
  Building2, Users, Receipt, Globe, LogOut, Activity, Stethoscope, ArrowLeft, Palette, ListChecks,
} from 'lucide-react';

export default function TenantAdminLayout() {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if ((user.role || '').toLowerCase() !== 'admin') {
      navigate('/dashboard', { replace: true }); return;
    }
    setReady(true);
  }, [user, navigate]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-500">Loading workspace…</p>
      </div>
    );
  }

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
      isActive
        ? 'bg-blue-50 text-blue-900 border border-blue-200'
        : 'text-zinc-600 hover:bg-zinc-100'
    }`;

  return (
    <div className="min-h-screen flex bg-zinc-50" data-testid="tenant-admin-layout">
      <aside className="w-64 border-r bg-white flex flex-col">
        <div className="px-5 py-5 border-b">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-700" />
            <span
              className="font-bold text-zinc-800"
              style={{ fontFamily: 'Manrope' }}
            >
              Clinic Admin
            </span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1 truncate">
            {tenant?.name || user?.email}
          </p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/admin/overview" className={linkClass} data-testid="ta-nav-overview">
            <Activity className="h-4 w-4" /> Overview
          </NavLink>
          <NavLink to="/admin/users" className={linkClass} data-testid="ta-nav-users">
            <Users className="h-4 w-4" /> Users
          </NavLink>
          <NavLink to="/admin/billing" className={linkClass} data-testid="ta-nav-billing">
            <Receipt className="h-4 w-4" /> Billing &amp; Plan
          </NavLink>
          <NavLink to="/admin/branding" className={linkClass} data-testid="ta-nav-branding">
            <Palette className="h-4 w-4" /> Branding
          </NavLink>
          <NavLink to="/admin/capabilities" className={linkClass} data-testid="ta-nav-capabilities">
            <ListChecks className="h-4 w-4" /> Capabilities
          </NavLink>
          <NavLink to="/admin/domains" className={linkClass} data-testid="ta-nav-domains">
            <Globe className="h-4 w-4" /> Custom Domains
          </NavLink>
          <NavLink to="/admin/settings" className={linkClass} data-testid="ta-nav-settings">
            <Building2 className="h-4 w-4" /> Clinic Info
          </NavLink>
        </nav>
        <div className="p-3 border-t space-y-1">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg"
            data-testid="ta-back-to-emr"
          >
            <ArrowLeft className="h-4 w-4" /> Open EMR · Appointments
          </button>
          <div className="text-[11px] text-zinc-500 pt-2 px-1">Signed in as</div>
          <div className="text-sm font-medium text-zinc-800 truncate px-1">{user?.email}</div>
          <button
            onClick={() => { logout(); }}
            className="mt-1 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
            data-testid="ta-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
